import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tooltip from '@components/core/Tooltip';
import StepsBreakdown from './StepsBreakdown';
import { STATUS_SUMMARY_VARIANT } from './constants';

// Design System — StatusSummary / StatusItem
// One status entry: colored dot + text (status name in the legend variant,
// applicant count in the counts variant). Display-only — wraps itself in
// core/Tooltip with the per-step breakdown when the item carries `steps`, behind a
// focusable trigger so the breakdown and its links open from the keyboard too.

const StatusItem = ({ item, variant, statusLabel }) => {
    const isCounts = variant === STATUS_SUMMARY_VARIANT.COUNTS;
    const text = isCounts ? item.count ?? 0 : statusLabel;
    const steps = item.steps || [];

    const content = (
        <>
            <span
                className={classNames('ds-status-summary__dot', `ds-status-summary__dot--${item.tone}`)}
                aria-hidden="true"
            />
            <span
                className={classNames('ds-status-summary__text', {
                    'ds-status-summary__text--bold': isCounts,
                })}
            >
                {text}
            </span>
            {/* Counts alone don't say what they count — name the status for screen readers. */}
            {isCounts && <span className="ds-status-summary__sr-only">{statusLabel}</span>}
        </>
    );

    // Tooltips belong to the counts variant only — legacy `Legends` gated its
    // breakdown with `!bar`, and the legend bar keeps that contract.
    const withTooltip = isCounts && steps.length > 0;

    return (
        <div
            className={classNames('ds-status-summary__item', `ds-status-summary__item--${item.status}`)}
            title={isCounts && !withTooltip ? statusLabel : undefined}
        >
            {withTooltip ? (
                <Tooltip title={<StepsBreakdown steps={steps} />}>
                    <span className="ds-status-summary__trigger" tabIndex={0}>{content}</span>
                </Tooltip>
            ) : (
                content
            )}
        </div>
    );
};

StatusItem.propTypes = {
    /** Normalised item: `{ status, tone, label, count, steps }`. */
    item: PropTypes.shape({
        status: PropTypes.string,
        tone: PropTypes.string,
        label: PropTypes.node,
        count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        steps: PropTypes.array,
    }).isRequired,
    /** Render mode inherited from the parent summary. */
    variant: PropTypes.oneOf(Object.values(STATUS_SUMMARY_VARIANT)).isRequired,
    /** Resolved, translated status name (item label / `labels` override / default). */
    statusLabel: PropTypes.node.isRequired,
};

export default StatusItem;
